// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HiddenProps } from "@insite/mobius/Hidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { screen } from "@rugby/XDL/Helpers";
import { color } from "@rugby/XDL/Utilities";
import { css } from "styled-components";

// main layout styles
interface XngProductDetailsStyles {
    topSection?: InjectableCss;
    productTableToggleWrapper?: HiddenProps;
    mainContentWrapper?: InjectableCss;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    bottomSection?: InjectableCss;
    stickyWrapper?: InjectableCss;
    stickyContainer?: InjectableCss;
    stickyPictureDesc?: InjectableCss;
    stickyAction?: InjectableCss;
    stickyHeroPicture?: InjectableCss;
    stickyProductDescription?: InjectableCss;
    stickyProductPrice?: InjectableCss;
    stickyProductActions?: InjectableCss;
    heroImage?: InjectableCss;
}

export const productDetailsStyles: XngProductDetailsStyles = {
    topSection: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
        `,
    },
    productTableToggleWrapper: {
        css: css`
            & > div {
                height: 100%;
                margin-right: 10px;
            }
        `,
    },
    mainContentWrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: row;
            ${screen("lte", "md")`
                flex-direction: column;
            `}
        `,
    },
    leftSection: {
        css: css`
            width: 580px;
            margin-right: 30px;
            position: relative;

            ${screen("lte", "md")`
                width: 100%;
            `}
        `,
    },
    rightSection: {
        css: css`
            flex: 1;
            ${screen("lte", "md")`
                width: 100%;
                margin-top: 20px;
            `}
            .productAvailabilityContainer {
                width: auto;
            }
        `,
    },
    heroImage: {
        css: css`
            img {
                max-height: 500px;
                object-fit: contain;
            }
        `,
    },
    bottomSection: {
        css: css``,
    },
    stickyWrapper: {
        css: css`
            opacity: 0;
            pointer-event: none;
            position: fixed;
            top: 0;
            left: 0;
            background: ${color("common.white")};
            z-index: 300;
            width: 100%;
            height: 0;
            box-shadow: 0 5px 10px -10px rgba(0, 0, 0, 0.2);
            display: none;

            &.show {
                opacity: 1;
                pointer-event: initial;
                min-height: 120px;
                height: auto;
                display: block;

                .disable-ATC {
                    button[data-test-selector="addProductToCart"] {
                        opacity: 0.5;
                        pointer-events: none;
                    }
                }
            }
        `,
    },
    stickyContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            margin: auto;
            padding: 20px 15px;

            & > div[class^="GridOffset"] {
                width: 100%;
                margin: 0;
                justify-content: space-between;
                ${screen("lte", "sm")`
                   flex-direction: column;
                `}
            }

            .spacer,
            .add-to-list,
            .brand-section {
                display: none !important;
            }
        `,
    },
    stickyPictureDesc: {
        css: css`
            display: flex;
            flex-direction: row;
            max-width: 50%;
            flex: 1;
            ${screen("lte", "sm")`
                max-width: 100%;
            `}
        `,
    },
    stickyAction: {
        css: css`
            flex: 1;
        `,
    },
    stickyHeroPicture: {
        css: css`
            width: 109px;
            height: 80px;
            margin-right: 20px;

            img {
                max-height: 80px;
                object-fit: contain;
            }

            & div {
                min-height: auto;
                margin: 0;
            }
        `,
    },
    stickyProductDescription: {
        css: css`
            margin-right: 20px;

            h2 {
                color: ${color("secondary.main")};
                font-family: Helvetica;
                font-size: 18px;
                letter-spacing: 0;
                line-height: 22px;
            }

            .ProductDetailsPartNumberText {
                font-size: 14px;
                & > span {
                    font-size: 14px;
                }
            }
        `,
    },
    stickyProductPrice: {
        css: css`
            margin-right: 20px;
            margin-bottom: 10px;

            & > div[class^="Wrapper"] {
                display: flex;
                flex-direction: row-reverse;

                & > div {
                    width: auto;
                    & > div[class^="Wrapper"] {
                        flex-direction: row-reverse;
                        align-items: center;
                        justify-content: flex-start;

                        .productPriceWrapper {
                            width: auto;
                            span {
                                font-size: 16px;
                            }
                            &.priceUnit {
                                margin-right: 20px;
                            }
                        }

                        .productAvailabilityContainer, .inventory-load-failed {
                            width: auto;
                            padding-right: 25px;
                        }

                        .lastPurchaseOnText {
                            display: none;
                        }
                    }
                }
            }
        `,
    },
    stickyProductActions: {
        css: css`
            & > div[class^="Wrapper"] > div > div[class^="Wrapper"] {
                justify-content: flex-end;
                button[data-test-selector="addProductToCart"] {
                    padding: 0 8px;
                }
            }
        `,
    },
};

// share styles
export interface ProductDetailsShareLinkStyles {
    wrapper?: InjectableCss;
}

export const shareLinkStyles: ProductDetailsShareLinkStyles = {
    wrapper: {
        css: css`
            margin-top: 30px;
            margin-bottom: 40px;
            text-align: center;
        `,
    },
};
